import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { RunnerRow } from "@/components/RunnerRow";
import { RaceDataStatus } from "@/components/RaceDataStatus";
import { RaceResult } from "@/components/RaceResult";
import { ResultEntry } from "@/components/admin/ResultEntry";
import { OddsEntry } from "@/components/admin/OddsEntry";
import { SectionalsPanel } from "@/components/admin/SectionalsPanel";
import { RailEntry } from "@/components/admin/RailEntry";
import { SpeedMap } from "@/components/SpeedMap";
import { TipsCard } from "@/components/analysis/TipsCard";
import { AnalysisDesk } from "@/components/analysis/AnalysisDesk";
import { useMember } from "@/components/MemberProvider";
import { useApi, ago } from "@/lib/api";
import { MeetingHeader } from "@/components/meeting/MeetingHeader";
import { RaceHeader, useNow, isFinished } from "@/components/meeting/RaceHeader";
import { raceMs, clock12 } from "@/lib/raceFormat";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
const toTips = () => scrollTo("race-tips");

const SORTS = { number: (a, b) => a.number - b.number, barrier: (a, b) => (a.barrier ?? 99) - (b.barrier ?? 99), odds: (a, b) => (a.odds ?? 999) - (b.odds ?? 999) };

export default function Meeting() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [m, setM] = useState(null);
  const [num, setNum] = useState(() => Number(params.get("race")) || 1);
  const [missing, setMissing] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("number");
  const { isAdmin } = useMember();
  const api = useApi();
  const [full, setFull] = useState(null);
  const now = useNow();
  const wanted = params.get("race");
  const load = useCallback(() => {
    api("get", `/meetings/${id}`).then((d) => {
      setM(d);
      setNum((n) => {
        const want = wanted == null ? n : Number(wanted);
        if (d.races.some((x) => x.number === want)) { setMissing(null); return want; }
        setMissing(wanted);
        return d.races[0]?.number || 1;
      });
    }).catch((e) => setM(e?.response?.status === 404 ? false : "error"));
    if (isAdmin) api("get", `/admin/meetings/${id}`).then(setFull).catch(() => {});
  }, [id, isAdmin, api, wanted]);
  useEffect(() => { load(); }, [load]);
  const pickRace = (n) => { setNum(n); setMissing(null); setParams({ race: String(n) }, { replace: true }); };

  const race = m?.races?.find((r) => r.number === num);
  const listRace = (isAdmin && full?.races?.find((r) => r.number === num)) || race;
  const picks = (race?.analysis?.selections || []).map((s) => s.number);
  const runners = useMemo(() => {
    const s = q.toLowerCase();
    return [...(listRace?.runners || [])]
      .filter((r) => !s || [r.horse, r.jockey, r.trainer].some((x) => (x || "").toLowerCase().includes(s)))
      .sort(SORTS[sort]);
  }, [listRace, q, sort]);

  if (m === "error") return <p className="p-10 text-center text-rose-300" data-testid="meeting-load-error">Couldn't load this meeting right now. Please try again shortly.</p>;
  if (m === false) return <p className="p-10 text-center text-slate-400" data-testid="meeting-not-found">Meeting not found.</p>;
  if (!m) return <p className="p-10 text-center text-slate-400">Loading…</p>;
  const adminRace = full?.races?.find((r) => r.number === num);
  const setAnalysis = (a) => {
    setFull({ ...full, races: full.races.map((r) => (r.number === num ? { ...r, analysis: a } : r)) });
    const pub = a.status === "published" ? { ...a, runners: a.published_runners || [] } : null;
    setM({ ...m, races: m.races.map((r) => (r.number === num ? { ...r, analysis: pub } : r)) });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/" data-testid="back-to-meetings" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" />All meetings</Link>
      <MeetingHeader m={m} onTips={toTips} />
      {isAdmin && <RailEntry m={m} onSaved={load} />}

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {m.races.map((r) => (
          <button key={r.number} data-testid={`race-tab-race-${r.number}`} onClick={() => pickRace(r.number)}
            className={`shrink-0 px-4 py-2 rounded-xl border text-left transition-colors ${num === r.number ? "bg-blue-600 border-blue-500 text-white" : "bg-[#131C31] border-[#202E4C] hover:border-blue-500/50"}`}>
            <span className="block font-display font-bold">R{r.number}</span>
            <span className="block text-[11px] text-slate-300">{clock12(raceMs(r, m.date))}</span>
          </button>
        ))}
      </div>
      {missing != null && <p className="mb-4 text-sm text-amber-300" data-testid="race-not-found">Race {missing} isn't on this card, so Race {num} is shown instead. Pick a race above.</p>}

      {race && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 surface overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-[#202E4C]">
              <RaceHeader race={race} date={m.date} now={now} onTips={toTips} onResults={() => scrollTo("race-result-block")} />
              {race.abandoned && <p className="text-sm text-rose-300 mt-1" data-testid="race-abandoned">This race has been abandoned.</p>}
              <div id="race-result-block" className="scroll-mt-24"><RaceResult result={race.result} pending={!race.abandoned && isFinished(race, raceMs(race, m.date), now)} /></div>
              <div className="flex flex-wrap gap-2 mt-4">
                <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[180px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input data-testid="runner-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search horse, jockey, trainer"
                    className="w-full pl-9 pr-3 py-2 rounded-full bg-[#0B101D] border border-white/10 text-sm outline-none focus:border-blue-500" />
                </div>
                {Object.keys(SORTS).filter((k) => k !== "odds" || listRace.runners.some((r) => r.odds)).map((k) => (
                  <button key={k} data-testid={`sort-${k}`} onClick={() => setSort(k)}
                    className={`px-3 py-2 rounded-full text-xs uppercase tracking-wider border transition-colors ${sort === k ? "bg-white text-slate-950 border-white" : "border-white/10 text-slate-400 hover:text-white"}`}>
                    {k}
                  </button>
                ))}
              </div>
              <RaceDataStatus meeting={m} race={race} isAdmin={isAdmin} onRefreshed={load} now={now} finished={isFinished(race, raceMs(race, m.date), now)} />
            </div>
            <div data-testid="runners-list">
              {runners.length ? runners.map((r) => <RunnerRow key={r.number} r={r} pick={picks.indexOf(r.number) + 1} />) : <p className="p-6 text-sm text-slate-400">No runners match.</p>}
            </div>
            {race.betfair?.updated_at && (
              <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-white/5" data-testid="betfair-attribution">
                Exchange back prices & silks: Betfair · updated {ago(race.betfair.updated_at)} · prices may be delayed, check with your bookmaker
              </p>
            )}
            {m.source === "krok" && (
              <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-white/5" data-testid="puntersedge-attribution">
                Fields, form, sectionals & track info{race.pe_market?.updated_at && race.pe_market.source !== "krok" ? <> · best bookmaker odds updated {ago(race.pe_market.updated_at)} (prices change, check with your bookmaker)</> : ""}: <a href="https://puntersedge.online" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">PuntersEdge</a>
                {race.pe_market?.source === "krok" && <span data-testid="krok-odds-attribution"> · Best bookmaker odds updated {ago(race.pe_market.updated_at)} (prices change, check with your bookmaker): <a href="https://krokodds.com.au" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">Krok Odds</a></span>}
              </p>
            )}
            {race.krok?.updated_at && (
              <p className="px-4 py-2 text-[11px] text-slate-500 border-t border-white/5" data-testid="krok-attribution">
                Silks & settling positions: Data provided by <a href="https://krokodds.com.au" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">Krok Odds</a> · updated {ago(race.krok.updated_at)}
              </p>
            )}
          </div>
          <div className="lg:col-span-4 scroll-mt-24" id="race-tips"><div className="lg:sticky lg:top-24"><TipsCard race={race} meeting={m} onUnlocked={load} /></div></div>
          <div className="lg:col-span-12"><SpeedMap meetingId={m.id} race={race} isAdmin={isAdmin} onSaved={load} /></div>
          {isAdmin && adminRace && <div className="lg:col-span-12"><SectionalsPanel meetingId={m.id} race={adminRace} /></div>}
          {isAdmin && adminRace && <div className="lg:col-span-12"><OddsEntry meetingId={m.id} race={adminRace} onSaved={load} /></div>}
          {isAdmin && adminRace && <div className="lg:col-span-12"><ResultEntry meetingId={m.id} race={adminRace} onSaved={load} /></div>}
          {isAdmin && adminRace && <div className="lg:col-span-12"><AnalysisDesk meetingId={m.id} race={adminRace} onUpdate={setAnalysis} /></div>}
        </div>
      )}
    </div>
  );
}
